import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  Checkbox,
  Thumbnail,
  DropZone,
  InlineError,
  InlineStack,
  Button,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import countries from "../../Data/countries.json";
import { ImagesIcon } from "@shopify/polaris-icons";

const AddEditCompetitor = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    name: "",
    location: "",
    website: "",
    ios_link: "",
    android_link: "",
    competitor_type: "",
    description: "",
    why_considered_competitor: "",
    image: "",
    is_active: true,
    priority: "",
    fb_followers: "",
    instagram: "",
    tiktok: "",
    estimated_sales: "",
    products: "",
    employees: "",
    instagram_followers: "",
    facebook: "",
    tiktok_followers: "",
  });

  const [errors, setErrors] = useState({
    name: "",
  });

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/competitors/${id}`)
      .then(() => {
        navigate(`/admin/competitors`);
      })
      .catch(console.error);
  };

  const validateFields = () => {
    let hasError = false;
    const newErrors = {};

    if (!item.name.trim()) {
      newErrors.name = "This field is required";
      hasError = true;
    }
    if (!item.location) {
      newErrors.location = "This field is required";
      hasError = true;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !hasError;
  };

  const [isImageUploading, setIsImageUploading] = useState(false);
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const [files, setFiles] = useState();
  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => setFiles(acceptedFiles[0]),
    []
  );

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );
  const [imageError, setImageError] = useState("");
  const imageKey = useRef("");

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "competitor");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setIsImageUploading(false);
        setItem({
          ...item,
          image:
            validImageTypes.indexOf(files[files.length - 1].type) > -1
              ? window.URL.createObjectURL(files[files.length - 1])
              : "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png",
        });
        setImageError("");
      })
      .catch(() => {});
  };
  // useEffect(() => {
  //   setOptionsCountries(
  //     Object.keys(countries).map((item, index) => {
  //       return {
  //         label: item,
  //         value: item,
  //       };
  //     })
  //   );
  // }, []);

  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/competitors/${id}`)
        .then((result) => {
          const data = result?.data?.data || {};
          setItem({
            name: data?.business_name || "",
            location: data?.location || "",
            website: data?.website || "",
            ios_link: data?.ios || "",
            android_link: data?.android || "",
            competitor_type: data?.type ? String(data?.type?.id) : "",
            description: data?.description || "",
            why_considered_competitor: data?.why_is_competitor || "",
            image: data?.logo || "",
            priority:
              data?.is_important != null ? String(data.is_important) : "",
            fb_followers: data?.facebook_followers ?? "",
            instagram_followers: data?.instagram_followers ?? "",
            tiktok_followers: data?.tiktok_followers ?? "",
            instagram: data?.instagram ?? "",
            facebook: data?.facebook ?? "",
            tiktok: data?.tiktok ?? "",
            estimated_sales: data?.estimated_sales ?? "",
            products: data?.products ?? "",
            employees: data?.employees ?? "",
            country: data?.country ?? "",
            // is_active: data?.is_active ? true : false,
          });
          setIsSaving(false);
        })
        .catch((err) => console.log(err));
    }
  }, []);

  const [error, setError] = useState("");

  const handleScreenshots = () => {
    navigate(`/admin/competitors/${id}/screenshots`);
  };
  const [optionsCountries, setOptionsCountries] = useState([]);
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        ];
        setOptionsCountries(options);
      })
      .catch((err) => console.log(err));
  }, []);

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Competitor`}
      backAction={{
        onAction: () => navigate(`/admin/competitors`),
      }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <TextField
            label="Name"
            value={item.name}
            onChange={(value) => handleChange("name", value)}
            error={errors.name}
          />
          <Select
            label="Country"
            options={optionsCountries}
            onChange={(value) => handleChange("location", value)}
            value={item.location}
            placeholder="Please select"
            error={errors?.location}
            styles={{
              // Fixes the overlapping problem of the compont
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
          />
          <Select
            label="Type"
            options={[
              { label: "Direct", value: "1" },
              { label: "Indirect", value: "2" },
              { label: "International", value: "3" },
            ]}
            value={item.competitor_type}
            onChange={(value) => handleChange("competitor_type", value)}
            placeholder="Please select"
            error={errors.type}
          />
          <Select
            label="Priority"
            options={[
              { label: "Important", value: "1" },
              { label: "Not Important", value: "0" },
            ]}
            value={item.priority}
            onChange={(value) => handleChange("priority", value)}
            placeholder="Please select"
          />

          {/* <TextField
            label="Location"
            value={item.location}
            onChange={(value) => handleChange("location", value)}
          /> */}

          <TextField
            label="Website"
            value={item.website}
            onChange={(value) => handleChange("website", value)}
          />

          <FormLayout.Group>
            <TextField
              label="iOS Link"
              value={item.ios_link}
              onChange={(value) => handleChange("ios_link", value)}
            />
            <TextField
              label="Android Link"
              value={item.android_link}
              onChange={(value) => handleChange("android_link", value)}
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <TextField
              label="Facebook Link"
              value={item.facebook}
              onChange={(value) => handleChange("facebook", value)}
            />
            <TextField
              label="Instagram Link"
              value={item.instagram}
              onChange={(value) => handleChange("instagram", value)}
            />
            <TextField
              label="Tiktok Link"
              value={item.tiktok}
              onChange={(value) => handleChange("tiktok", value)}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TextField
              label="Facebook Followers"
              value={item.fb_followers}
              type="number"
              onChange={(value) => handleChange("fb_followers", value)}
            />
            <TextField
              label="Instagram Followers"
              value={item.instagram_followers}
              type="number"
              onChange={(value) => handleChange("instagram_followers", value)}
            />
            <TextField
              label="Tiktok Followers"
              value={item.tiktok_followers}
              type="number"
              onChange={(value) => handleChange("tiktok_followers", value)}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TextField
              label="Products"
              value={item.products}
              type="number"
              onChange={(value) => handleChange("products", value)}
            />
            <TextField
              label="Employees"
              value={item.employees}
              type="number"
              onChange={(value) => handleChange("employees", value)}
            />
            <TextField
              label="Estimated Sales"
              value={item.estimated_sales}
              type="number"
              onChange={(value) => handleChange("estimated_sales", value)}
            />
          </FormLayout.Group>

          <TextField
            label="Description"
            value={item.description}
            onChange={(value) => handleChange("description", value)}
            multiline={4}
          />

          <TextField
            label="Why Considered Competitor"
            value={item.why_considered_competitor}
            onChange={(value) =>
              handleChange("why_considered_competitor", value)
            }
            multiline={3}
          />

          <Text as="p" fontWeight="regular">
            Logo
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.image ? item.image : ""} />
            </div>
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={imageError} />

          {/* <Checkbox
            label="Is active"
            checked={item.is_active}
            onChange={(value) => handleChange("is_active", value)}
          /> */}
          <InlineError message={error} />
          <Button
            fullWidth
            variant="primary"
            onClick={() => handleScreenshots(item.id)}
            icon={ImagesIcon}
          >
            Screenshots
          </Button>
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
            loading: isSaving,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading ? <LoadingScreen /> : null}
    </Page>
  );

  function handleSave() {
    if (!validateFields()) {
      return;
    }
    setIsSaving(true);
    const formData = new FormData();
    mode === "edit" && formData.append("_method", "PATCH");
    formData.append("business_name", item.name);
    formData.append("location", item.location);
    formData.append("website", item.website);
    formData.append("ios", item.ios_link);
    formData.append("android", item.android_link);
    formData.append("type", item.competitor_type);
    formData.append("description", item.description);
    formData.append("why_is_competitor", item.why_considered_competitor);
    formData.append("is_important", Number(item.priority));
    formData.append("tiktok_followers", item.tiktok_followers);
    formData.append("facebook_followers", item.fb_followers);
    formData.append("instagram_followers", item.instagram_followers);
    formData.append("instagram", item.instagram);
    formData.append("facebook", item.facebook);
    formData.append("tiktok", item.tiktok);
    formData.append("estimated_sales", item.estimated_sales);
    formData.append("products", item.products);
    formData.append("products", item.products);
    formData.append("employees", item.employees);
    imageKey.current && formData.append("business_logo", imageKey.current);

    // formData.append("is_active", item.is_active ? 1 : 0);

    axios
      .post(mode === "new" ? "/competitors" : `/competitors/${id}`, formData)
      .then(() => {
        mode === "new" ? navigate("/admin/competitors") : toggleActive();
      })
      .catch((error) => {
        console.log(error.response);
        setIsSaving(false);
        if (error.response && error.response.data?.message) {
          console.log("error=", error.response.data?.message);
          const serverErrors = error.response.data.message;
          setError(serverErrors);
        } else {
          console.error(error);
        }
      })
      .finally(() => setIsSaving(false));
  }
};

export default AddEditCompetitor;
